import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, withApollo } from '@apollo/client/react/hoc';
import { compose } from 'redux';
import { Switch, Redirect } from 'react-router';

import getTrackingCookie from 'util/adwords';
import { validateForm } from 'util/forms';
import { track, analyticsEvents, trackPPCSignUp } from 'util/analytics';
import { trackQuestionnaireSubmit } from 'util/ga4';
import { getCookie } from 'util/cookies';

import Route from 'components/Route';
import SignInModal from 'components/ClientSearch/Modal/NewSignInModal';
import withUser from 'components/withUser';
import { update, updatePhase, screens, nextPage } from 'actions/onboarding';
import { updateSearch } from 'actions/search';
import RenterOnboardingMutation from 'queries/mutations/RenterOnboardingMutation.gql';
import CurrentUserQuery from 'queries/CurrentUserQuery.gql';
import { splitQueryString } from 'util/strings';
import { Helmet } from 'react-helmet';
import styles from './style.module.scss';
import SizePage from './SizePage';
import LocationPage from './LocationPage';
import BudgetPage from './BudgetPage';
import TimeframesPage from './TimeframesPage';
import ResultsPage from './ResultsPage';
import ProfileSidebar from './ProfileSidebar';
import ProfileHeader from './ProfileHeader';
import ProfileFooter from './ProfileFooter';
import Geolocate from './Geolocate';

export class OnboardingPage extends PureComponent {
  state = {
    showSignInForUser: undefined,
  };

  static propTypes = {
    phase: PropTypes.string.isRequired,
    nextPage: PropTypes.func.isRequired,
    currentUser: PropTypes.object,
    renterOnboardingMutation: PropTypes.func.isRequired,
    update: PropTypes.func.isRequired,
    updatePhase: PropTypes.func.isRequired,
    login: PropTypes.func.isRequired,
    updateSearch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string.isRequired,
    }).isRequired,
    lastActiveScreenIndex: PropTypes.number.isRequired,
    client: PropTypes.shape({
      query: PropTypes.func.isRequired,
    }).isRequired,
    onboardingState: PropTypes.shape({
      areasOfInterest: PropTypes.array,
      email: PropTypes.string,
      name: PropTypes.string,
      companyName: PropTypes.string,
      phone: PropTypes.string,
      renterComments: PropTypes.string,
      city: PropTypes.string,
      numEmployees: PropTypes.number,
      budgetPsf: PropTypes.number,
      totalSquareFeet: PropTypes.number,
      leaseTerm: PropTypes.string,
      moveInDate: PropTypes.string,
    }).isRequired,
  };

  static defaultProps = {
    currentUser: null,
  };

  static contextTypes = {
    store: PropTypes.any,
  };

  componentDidMount() {
    if (
      this.props.phase !== 'COMPLETED' &&
      this.props.phase !== 'IN_PROGRESS'
    ) {
      this.props.updatePhase('IN_PROGRESS');
    }

    const queryParams = splitQueryString(this.props.location.search);
    const numEmployeesParam = queryParams && queryParams.numEmployees;
    const currentPath = this.props.location.pathname;

    if (numEmployeesParam && currentPath === '/profile/size') {
      this.props.update({ numEmployees: numEmployeesParam });
      this.props.nextPage();
    }
  }

  get selectedSubmarkets() {
    return this.props.onboardingState.areasOfInterest.filter(
      (area) => area.__typename === 'Submarket'
    );
  }

  get selectedNeighborhoods() {
    return this.props.onboardingState.areasOfInterest.filter(
      (area) => area.__typename === 'Neighborhood'
    );
  }

  get selectedMarket() {
    return this.props.onboardingState.areasOfInterest.filter(
      (area) => area.__typename === 'Market'
    )[0];
  }

  onCloseSignIn = (currentUser) => {
    if (currentUser) {
      this.props.update({ email: currentUser.email });
      this.setState({ showSignInForUser: currentUser.email });
      this.props.history.push('/');
    } else {
      this.setState({ showSignInForUser: undefined });
    }
  };

  onFinish = (evt) => {
    evt.preventDefault();

    // Only process submission from the last page
    const activeScreenIndex = screens.findIndex(
      (screen) => screen.link === this.props.location.pathname
    );
    const isLastPage = activeScreenIndex === screens.length - 1;
    if (!isLastPage) return;

    if (!validateForm(this.formRef)) return;

    const passwordInput = this.formRef.elements.password;
    const password = passwordInput ? passwordInput.value : '';
    const onboardingData = {
      serverId: getCookie('SERVERID') ? getCookie('SERVERID') : 'unidentified',
      email: this.props.currentUser?.email || this.props.onboardingState.email,
      password,
      name: this.props.onboardingState.name,
      companyName: this.props.onboardingState.companyName,
      phone: this.props.onboardingState.phone,
      renterComments: this.props.onboardingState.renterComments,
      marketOfInterest: this.selectedMarket.id,
      numberOfEmployees: this.props.onboardingState.numEmployees,
      squareFeet: this.props.onboardingState.totalSquareFeet,
      neighborhoodIds: this.selectedNeighborhoods.map((nhood) => nhood.id),
      pricePerSquareFoot: this.props.onboardingState.budgetPsf,
      leaseTerm: this.props.onboardingState.leaseTerm,
      moveInDateRange: this.props.onboardingState.moveInDate,
      gclid: getTrackingCookie(),
      signUpType: 'Onboarding',
      googleAnalyticsId: getCookie('_gid'),
      areaExternalIds: this.props.onboardingState.areasOfInterest
        .filter((area) => area.__typename !== 'Market')
        .map(({ externalId }) => externalId),
    };

    this.props
      .renterOnboardingMutation({
        variables: onboardingData,
        refetchQueries: [
          {
            query: CurrentUserQuery,
          },
        ],
      })
      .then(({ data }) => {
        if (data.renterOnboarding.errors) {
          this.setState({
            showSignInForUser: this.props.onboardingState.email,
          });
        } else {
          if (getTrackingCookie()) trackPPCSignUp();
          track(analyticsEvents.sign_up, { source: onboardingData.signUpType });
          trackQuestionnaireSubmit({
            employees: this.props.onboardingState.numEmployees,
            space: this.props.onboardingState.totalSquareFeet,
            location: this.props.onboardingState.city,
            budget: this.props.onboardingState.budgetPsf,
          });
          this.props.update({
            onboardingId: data.renterOnboarding.onboardingId,
          });
          this.props.updatePhase('COMPLETED');
          this.props.login(data.renterOnboarding.currentUser);

          let numEmployeesConstant = '101+';
          if (this.props.onboardingState.numEmployees <= 10) {
            numEmployeesConstant = '1-10';
          } else if (this.props.onboardingState.numEmployees <= 25) {
            numEmployeesConstant = '11-25';
          } else if (this.props.onboardingState.numEmployees <= 50) {
            numEmployeesConstant = '26-50';
          } else if (this.props.onboardingState.numEmployees <= 100) {
            numEmployeesConstant = '51-100';
          }
          this.props.updateSearch({
            cityName: this.props.onboardingState.city,
            neighborhoods: this.selectedNeighborhoods.map((n) => n.name),
            numEmployees: numEmployeesConstant,
          });

          this.props.history.push('/tourbook/search');
        }
      });
  };

  render() {
    const { lastActiveScreenIndex } = this.props;
    const activeScreenIndex = this.props.history
      ? screens.findIndex(
        (screen) => screen.link === this.props.location.pathname
      )
      : 0;
    const activeScreen = screens[activeScreenIndex] || screens[0];
    const isLastPage = activeScreenIndex === screens.length - 1;

    return (
      <div
        className={`${styles.container} ${activeScreen.className ? styles[activeScreen.className] : ''
          }`}
      >
        <Helmet>
          <link rel="canonical" href="https://www.squarefoot.com/profile" />
        </Helmet>
        {this.props.location.pathname === '/profile/size' &&
          !this.props.location.search.includes('noOfEmployees') && (
            <Geolocate />
          )}
        {this.state.showSignInForUser && (
          <SignInModal
            existingEmail={this.state.showSignInForUser}
            onClose={this.onCloseSignIn}
            onSuccess={this.onCloseSignIn}
          />
        )}
        <ProfileSidebar activeScreenIndex={activeScreenIndex} />
        <main
          className={`${styles.main} ${activeScreenIndex === screens.length - 1 ? styles.finalPage : ''
            }`}
        >
          <form
            onSubmit={this.onFinish}
            className={`${styles.content} onboarding`}
            ref={(ref) => {
              this.formRef = ref;
            }}
          >
            {!isLastPage && (
              <ProfileHeader
                progress={(lastActiveScreenIndex + 1) / (screens.length + 1)}
              />
            )}
            <Switch>
              <Route
                path="/profile/size"
                component={SizePage}
                options={{ showHeader: false, showFooter: false }}
              />
              <Route
                path="/profile/location"
                component={LocationPage}
                options={{ showHeader: false, showFooter: false }}
              />
              <Route
                path="/profile/budget"
                component={BudgetPage}
                options={{ showHeader: false, showFooter: false }}
              />
              <Route
                path="/profile/timeframes"
                component={TimeframesPage}
                options={{ showHeader: false, showFooter: false }}
              />
              <Route
                path="/profile/results"
                component={ResultsPage}
                options={{ showHeader: false, showFooter: false }}
              />
              <Redirect from="/profile/" to="/profile/size" />
            </Switch>
          </form>
          {!isLastPage && (
            <ProfileFooter activeScreenIndex={activeScreenIndex} />
          )}
        </main>
      </div>
    );
  }
}

export default compose(
  connect(
    (state) => ({
      lastActiveScreenIndex: state.onboarding.lastActiveScreenIndex,
      phase: state.onboarding.phase,
      onboardingState: state.onboarding,
    }),
    {
      update,
      updatePhase,
      updateSearch,
      nextPage,
    }
  ),
  withUser,
  graphql(RenterOnboardingMutation, { name: 'renterOnboardingMutation' }),
  withApollo
)(OnboardingPage);
